import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Entry, Visit } from '@/types/api';
import { deriveChart } from '@/utils/toothState';
import { changedTeeth, visitTimeline } from '@/utils/visitHistory';

/**
 * Browsing earlier visits. `index` is a position in the timeline (oldest
 * first) or null for the current state. `past` is the chart right after the
 * selected visit and the teeth that changed in it.
 */
export function useVisitHistory(
  visits: readonly Visit[] | undefined,
  entries: readonly Entry[],
  onEnterPast: () => void
) {
  const [index, setIndex] = useState<number | null>(null);
  const timeline = useMemo(() => visitTimeline(visits ?? [], entries), [visits, entries]);
  // A shorter timeline (data reloaded) must not leave the index dangling.
  useEffect(() => {
    if (index !== null && index >= timeline.length) setIndex(null);
  }, [index, timeline.length]);
  const step = index === null ? null : (timeline[index] ?? null);
  const past = useMemo(
    () =>
      step
        ? {
            // Work performed in that visit shows green, as it did then.
            chart: deriveChart(step.upTo, (e) => e.visitId === step.visit.id),
            changed: changedTeeth(step),
          }
        : null,
    [step]
  );

  const show = useCallback(
    (next: number | null) => {
      setIndex(next);
      if (next !== null) onEnterPast();
    },
    [onEnterPast]
  );

  return { timeline, index, step, past, show };
}
